class NewProductCard extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.addEventListener("linked-product-change", (e) =>
      this.handleProductChange(e)
    );
    this.quickViewElement = this.querySelector("quick-view");
    this.templateName = this.dataset.template ?? "product-card";
    this.quickAddTriggerElement = this.querySelector(
      "[data-quick-add-trigger]"
    );
    if (this.quickViewElement) {
      this.querySelectorAll(".product-card-gallery__image-container").forEach(
        (el) => {
          el.addEventListener("click", (e) => {
            e.preventDefault();
            this.quickViewElement.classList.add("active");
          });
        }
      );
    }
    this.addEventListener("click", (e) => {
      if (e.target.closest("[data-quick-add-trigger]")) {
        e.preventDefault();
        this.querySelector("quick-add")?.classList.add("active");
      }
    });
  }

  handleProductChange(e) {
    const productUrl = e.detail.productUrl;
    this.classList.add("loading");
    fetch(productUrl + "?view=" + this.templateName)
      .then((res) => res.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const productCard = doc.querySelector("new-product-card");
        this.innerHTML = productCard.innerHTML;
        this.classList.remove("loading");
        this.init();
      });
  }
}

if (!window.customElements.get("new-product-card")) {
  customElements.define("new-product-card", NewProductCard);
}
class ProductCardGallery extends HTMLElement {
  constructor() {
    super();
    this.activate = this.activate.bind(this);
    this.handleNavigationClick = this.handleNavigationClick.bind(this);
  }

  connectedCallback() {
    if (this.dataset.galleryImages === undefined) {
      this.initSwiper();
      return;
    }
    this.addEventListener("pointerenter", this.activate);
    this.addEventListener("pointerdown", this.activate, true);
    this.addEventListener("focusin", this.activate);
    this.addEventListener("click", this.handleNavigationClick, true);
  }

  disconnectedCallback() {
    this.removeEventListener("pointerenter", this.activate);
    this.removeEventListener("pointerdown", this.activate, true);
    this.removeEventListener("focusin", this.activate);
    this.removeEventListener("click", this.handleNavigationClick, true);
    this.swiper?.destroy(true, true);
    this.swiper = null;
  }

  handleNavigationClick(event) {
    if (event.target.closest("[data-prev-btn], [data-next-btn]")) {
      event.preventDefault();
      this.activate();
    }
  }

  activate() {
    if (this.swiper || !this.getClientRects().length) return;
    const wrapper = this.querySelector(".swiper-wrapper");
    if (!wrapper || typeof Swiper === "undefined") return;
    if (this.dataset.galleryImages !== undefined) {
      const images = JSON.parse(this.dataset.galleryImages);
      const fragment = document.createDocumentFragment();
      images.forEach((image) => {
        const slide = document.createElement("div");
        slide.className = "swiper-slide";
        const container = document.createElement("div");
        container.className = "product-card-gallery__image-container";
        const img = document.createElement("img");
        img.className = "w-full h-auto aspect-[375/530] object-cover";
        img.loading = "lazy";
        img.alt = image.alt || "";
        img.width = image.width;
        img.height = image.height;
        img.src = image.src;
        container.appendChild(img);
        const quickView = this.closest("new-product-card")?.querySelector(
          "quick-view"
        );
        if (quickView) {
          container.addEventListener("click", (event) => {
            event.preventDefault();
            quickView.classList.add("active");
          });
        }
        slide.appendChild(container);
        fragment.appendChild(slide);
      });
      wrapper.appendChild(fragment);
      delete this.dataset.galleryImages;
    }
    this.initSwiper();
  }

  initSwiper() {
    if (this.swiper) return;
    const prevBtn = this.querySelector("[data-prev-btn]");
    const nextBtn = this.querySelector("[data-next-btn]");
    this.swiper = new Swiper(this.querySelector(".swiper"), {
      slidesPerView: 1,
      spaceBetween: 10,
      pagination: {
        el: this.querySelector(".swiper-pagination"),
        clickable: true,
      },
      loop: this.querySelectorAll(".swiper-slide").length > 1,
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn,
      },
      a11y: {
        enabled: true,
        prevSlideMessage:
          prevBtn?.getAttribute("aria-label") || "Go to the previous slide",
        nextSlideMessage:
          nextBtn?.getAttribute("aria-label") || "Go to the next slide",
      },
    });
  }
}
if (!window.customElements.get("product-card-gallery")) {
  customElements.define("product-card-gallery", ProductCardGallery);
}

class LinkedProductSwatch extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.addEventListener("click", (e) => {
      e.preventDefault();
      this.dispatchEvent(
        new CustomEvent("linked-product-change", {
          detail: {
            productUrl: this.dataset.productUrl,
          },
          bubbles: true,
        })
      );
    });
  }
}

if (!window.customElements.get("linked-product-swatch")) {
  customElements.define("linked-product-swatch", LinkedProductSwatch);
}

class FeaturedCollectionSwiper extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.initSwiper();
  }

  initSwiper() {
    const prevBtn = this.querySelector("[data-fc-prev-btn]");
    const nextBtn = this.querySelector("[data-fc-next-btn]");
    this.swiper = new Swiper(this, {
      slidesPerView: 1.2,
      spaceBetween: 10,
      slidesOffsetBefore: 16,
      mousewheel: {
        forceToAxis: true
      },
      freeMode: {
        enabled: true,
      },
      breakpoints: {
        767: {
          slidesPerView: 3.8,
          slidesOffsetBefore: 20,
        },
      },
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn,
      },
    });
  }
}
if (!window.customElements.get("featured-collection-swiper")) {
  customElements.define("featured-collection-swiper", FeaturedCollectionSwiper);
}

class RelatedProductsSwiper extends HTMLElement {
  connectedCallback() {
    this.initSwiper();
  }

  initSwiper() {
    const prevBtn = this.querySelector("[data-rp-prev-btn]");
    const nextBtn = this.querySelector("[data-rp-next-btn]");
    this.swiper = new Swiper(this, {
      slidesPerView: 1.2,
      spaceBetween: 10,
      slidesOffsetBefore: 16,
      mousewheel: {
        forceToAxis: true,
      },
      freeMode: {
        enabled: true,
      },
      breakpoints: {
        767: {
          slidesPerView: 3.8,
          slidesOffsetBefore: 20,
        },
      },
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn,
      },
    });
  }
}
if (!window.customElements.get("related-products-swiper")) {
  customElements.define("related-products-swiper", RelatedProductsSwiper);
}

class FeaturedCollections extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.triggerElements = this.querySelectorAll("[data-collection-trigger]");
    this.targetElements = this.querySelectorAll("[data-collection-target]");
    this.triggerElements.forEach((el) => {
      el.addEventListener("click", (e) => {
        e.preventDefault();
        this.handleTriggerClick(e);
      });
    });
  }

  handleTriggerClick(e) {
    const collectionId = e.target.dataset.collectionTrigger;
    this.targetElements.forEach((el) => {
      el.classList.add("!hidden");
    });
    const collection = this.querySelector(
      `[data-collection-target="${collectionId}"]`
    );
    collection.classList.remove("!hidden");

    // Tabs other than the first arrive as a template; build one the first time it's shown. This runs after the
    // tab is visible so its swiper measures a real width when it connects.
    const template = collection.querySelector(":scope > template[data-collection-template]");
    if (template) {
      collection.appendChild(template.content.cloneNode(true));
      template.remove();
    }
    this.triggerElements.forEach((el) => {
      el.classList.remove("underline");
    });
    e.target.classList.add("underline");
  }
}

if (!window.customElements.get("featured-collections")) {
  customElements.define("featured-collections", FeaturedCollections);
}

class QuickAdd extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.quickAddTriggerElement = this.querySelector(
      "[data-quick-add-trigger]"
    );
    this.quickAddTriggerElement?.addEventListener("click", (e) => {
      e.preventDefault();
      this.show();
    });
    this.querySelectorAll("[data-quick-add-close]").forEach((closeButton) => {
      closeButton.addEventListener("click", () => {
        this.hide();
        this.quickAddTriggerElement?.focus();
      });
    });
    this.addEventListener("mouseleave", () => {
      if (window.matchMedia("(hover: hover)").matches) {
        this.hide();
      }
    });

    // One shared scroll listener closes whichever quick add is open, rather than one listener per card.
    if (!QuickAdd.scrollListenerAdded) {
      QuickAdd.scrollListenerAdded = true;
      document.addEventListener(
        "scroll",
        () => {
          document.querySelectorAll("quick-add.active").forEach((quickAdd) => quickAdd.hide());
        },
        { passive: true }
      );
    }
  }

  show() {
    document.querySelectorAll("quick-add.active").forEach((quickAdd) => {
      if (quickAdd !== this) {
        typeof quickAdd.hide === "function"
          ? quickAdd.hide()
          : quickAdd.classList.remove("active");
      }
    });
    this.classList.add("active");
    document.documentElement.classList.add("quick-add-open");
  }

  hide() {
    this.classList.remove("active");
    if (!document.querySelector("quick-add.active")) {
      document.documentElement.classList.remove("quick-add-open");
    }
  }
}

if (!window.customElements.get("quick-add")) {
  customElements.define("quick-add", QuickAdd);
}

class QuickAddForm extends HTMLElement {
  constructor() {
    super();
  }

  get formId() {
    return this.getAttribute("id");
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.form = this.querySelector("form");
    this.masterSelect = this.querySelector("[name='id']");
    this.bindEvents();
  }

  bindEvents() {
    this.addEventListener(
      "variant:change",
      this.handleVariantChange.bind(this)
    );
    this.form.addEventListener("submit", this.handleSubmit.bind(this));

    // The variant picker fires add-to-cart as soon as the last option is
    // picked, which is what quick add wants. A bundle card commits on its own
    // button instead, so picking a size there only sets the variant.
    if (!this.isBundleForm) {
      this.addEventListener("add-to-cart", this.handleSubmit.bind(this));
    }
  }

  handleVariantChange(event) {
    const variant = event.detail.variant;
    if (this.isBundleForm && !this.variantCanBeBundled(variant)) {
      this.masterSelect.value = "";
      this.setBundleButtonEnabled(false);
      return;
    }
    this.masterSelect.value = variant.id;
    if (this.isBundleForm) this.setBundleButtonEnabled(true);
  }

  variantCanBeBundled(variant) {
    return Boolean(variant && variant.available !== false);
  }

  setBundleButtonEnabled(enabled) {
    const button = this.querySelector("[data-atc-button]");
    if (button) button.disabled = !enabled;
  }

  get isBundleForm() {
    return this.dataset.formType === "bundle-card-form";
  }

  get rowIndex() {
    return this.closest("[data-bundle-row-index]").dataset.bundleRowIndex;
  }

  handleSubmit(event) {
    event.preventDefault();
    if (this.isBundleForm) {
      this.addToBundle();
    } else {
      this.submit();
    }
  }

  addToBundle() {
    const variants = JSON.parse(
      this.querySelector("[data-variants]").textContent
    );
    const variant = variants.find((v) => v.id == this.masterSelect.value);
    if (!this.variantCanBeBundled(variant)) return;

    const product = JSON.parse(
      this.closest("[data-product-id]").querySelector("[data-product]")
        .textContent
    );

    this.dispatchEvent(
      new CustomEvent("addToBundle", {
        bubbles: true,
        detail: {
          rowIndex: this.rowIndex,
          product,
          variant: {
            id: variant.id,
            imageUrl: variant.featured_image
              ? variant.featured_image.src
              : product.featured_image,
            title: variant.title,
            price: variant.price,
            options: variant.options,
          },
        },
      })
    );
  }

  submit() {
    const formData = new FormData(this.form);
    formData.append("id", this.masterSelect.value);
    formData.append("quantity", 1);
    document.documentElement.dispatchEvent(
      new CustomEvent("theme:loading:start", { bubbles: true })
    );

    fetch("/cart/add.js", {
      method: "POST",
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        document.dispatchEvent(
          new CustomEvent("cart:refresh", {
            bubbles: true,
            detail: {
              baseEvent: "variant:add",
            },
          })
        );
        this.closest("quick-add")?.hide();
        document.querySelector('cart-drawer')?.show();
      })
      .catch((err) => console.error(err))
      .finally(() => {
        document.documentElement.dispatchEvent(
          new CustomEvent("theme:loading:end", { bubbles: true })
        );
      });
  }
}

if (!window.customElements.get("quick-add-form")) {
  window.customElements.define("quick-add-form", QuickAddForm);
}

class NewVariantPicker extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.variants = JSON.parse(
      this.querySelector("[data-variants]").textContent
    );
    this.currentVariant = null;
    this.optionInputs = this.querySelectorAll("input");
    this.bindEvents();
  }

  bindEvents() {
    this.listenToChange();
  }

  listenToChange() {
    this.optionInputs.forEach((input) =>
      input.addEventListener("change", (e) => {
        e.preventDefault();
        this.updateCurrentVariant(e.target);
      })
    );
  }

  updateCheckedInputs() {
    this.optionInputs.forEach((input) => {
      if (input.checked) {
        input.setAttribute("checked", "checked");
      } else {
        input.removeAttribute("checked");
      }
    });
  }

  getSelectedValues() {
    return Array.from(this.querySelectorAll("input:checked")).reduce(
      (acc, input) => {
        acc[input.name] = input.value;
        return acc;
      },
      {}
    );
  }

  updateCurrentVariant(input = null) {
    const isAddOnClick = input?.dataset.addOnClick == "true";
    this.updateCheckedInputs();
    const selectedValues = this.getSelectedValues();

    this.currentVariant =
      this.variants.find((variant) => {
        return Object.entries(selectedValues).every(([name, value]) => {
          return variant[name] === value;
        });
      }) || null;

    this.querySelectorAll("[data-option-name-value]").forEach((display) => {
      const checked = display
        .closest(".quick-add-option")
        .querySelector("input:checked");
      display.textContent = checked ? checked.value : "";
    });

    if (this.currentVariant) {
      this.dispatchEvent(
        new CustomEvent("variant:change", {
          detail: {
            variant: this.currentVariant,
          },
          bubbles: true,
        })
      );
    }

    if (isAddOnClick) {
      this.dispatchEvent(
        new CustomEvent("add-to-cart", {
          bubbles: true,
        })
      );
    }
  }
}

if (!window.customElements.get("new-variant-picker")) {
  window.customElements.define("new-variant-picker", NewVariantPicker);
}

class FeaturedCollectionList extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.triggerElements = this.querySelectorAll("[data-collection-trigger]");
    this.targetElements = this.querySelectorAll("[data-collection-target]");
    this.triggerElements.forEach((el) => {
      el.addEventListener("mouseover", (e) => {
        this.handleTriggerMouseover(e);
      });
    });
  }

  handleTriggerMouseover(e) {
    this.targetElements.forEach((el) => {
      el.classList.add("hidden");
    });
    const target = this.querySelector(
      `[data-collection-target="${e.target.dataset.collectionTrigger}"]`
    );
    target.classList.remove("hidden");
  }
}

if (!window.customElements.get("featured-collection-list")) {
  window.customElements.define(
    "featured-collection-list",
    FeaturedCollectionList
  );
}

class ReviewCardsSwiper extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.initSwiper();
  }

  initSwiper() {
    this.swiper = new Swiper(this, {
      slidesPerView: 1.2,
      spaceBetween: 10,
      slidesOffsetBefore: 16,
      pagination: {
        el: this.querySelector(".swiper-pagination"),
        clickable: true,
      },
      breakpoints: {
        767: {
          slidesPerView: 3.7,
          slidesOffsetBefore: 20,
        },
      },
    });
  }
}

if (!window.customElements.get("review-cards-swiper")) {
  window.customElements.define("review-cards-swiper", ReviewCardsSwiper);
}

class CardsSwiper extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initSwiper();
  }

  initSwiper() {
    this.swiper = new Swiper(this, {
      slidesPerView: 1.15,
      spaceBetween: 10,
      slidesOffsetBefore: 16,
      slidesOffsetAfter: 16,
      mousewheel: {
        forceToAxis: true
      },
      pagination: {
        el: this.querySelector(".swiper-pagination"),
        clickable: true,
      },
      breakpoints: {
        767: {
          slidesPerView: 3.15,
          spaceBetween: 16,
          slidesOffsetBefore: 20,
          slidesOffsetAfter: 20,
        },
      },
    });
  }
}

if (!window.customElements.get("cards-swiper")) {
  window.customElements.define("cards-swiper", CardsSwiper);
}

class TeamSwiper extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.initSwiper();
  }

  initSwiper() {
    this.swiper = new Swiper(this, {
      slidesPerView: 1.15,
      spaceBetween: 10,
      slidesOffsetBefore: 16,
      slidesOffsetAfter: 16,
      mousewheel: {
        forceToAxis: true,
      },
      pagination: {
        el: this.querySelector(".swiper-pagination"),
        clickable: true,
      },
      scrollbar: {
        el: this.querySelector(".swiper-scrollbar"),
        draggable: true,
      },
      breakpoints: {
        767: {
          slidesPerView: 3.15,
          spaceBetween: 10,
          slidesOffsetBefore: 20,
          slidesOffsetAfter: 20,
        },
      },
    });
  }
}

if (!window.customElements.get("team-swiper")) {
  window.customElements.define("team-swiper", TeamSwiper);
}

class SlideshowSwiper extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.initSwiper();
  }

  initSwiper() {
    if (this.swiper) {
      return;
    }

    if (!window.Swiper) {
      window.addEventListener("load", () => this.initSwiper(), { once: true });
      return;
    }

    this.classList.add("swiper");

    const wrapper = this.querySelector(".swiper-wrapper");
    if (!wrapper) {
      return;
    }

    const slides = wrapper.querySelectorAll(".swiper-slide");
    if (slides.length === 0) {
      requestAnimationFrame(() => this.initSwiper());
      return;
    }

    if (!this.querySelector(".swiper-pagination")) {
      const pagination = document.createElement("div");
      pagination.className = "swiper-pagination slideshow-pagination";
      pagination.setAttribute("aria-hidden", "true");
      this.appendChild(pagination);
    }

    const paginationEl = this.querySelector(".swiper-pagination");

    this.swiper = new Swiper(this, {
      slidesPerView: 1,
      loop: slides.length > 1,
      pagination: {
        el: paginationEl,
        clickable: true,
        type: "bullets",
      },
      watchOverflow: false,
      // No observer/observeParents: the slides are fixed in the markup, and the theme editor re-renders the whole
      // section on change, so the extra MutationObservers were only adding main-thread work.
      autoplay: {
        delay: this.dataset.slideshowSpeed * 1000,
      },
    });

    this.swiper.update();
    if (this.swiper.pagination && paginationEl) {
      this.swiper.pagination.render();
      this.swiper.pagination.update();

      if (!paginationEl.querySelector(".swiper-pagination-bullet")) {
        this.initManualPagination(paginationEl, slides.length);
      }
    } else if (paginationEl) {
      this.initManualPagination(paginationEl, slides.length);
    }
  }

  initManualPagination(paginationEl, slideCount) {
    if (!this.swiper || paginationEl.dataset.manualPagination === "true") {
      return;
    }

    paginationEl.dataset.manualPagination = "true";
    paginationEl.innerHTML = "";

    for (let i = 0; i < slideCount; i += 1) {
      const dot = document.createElement("button");
      dot.type = "button";
      dot.className = "slideshow-pagination__dot";
      dot.setAttribute("aria-label", `Go to slide ${i + 1}`);
      dot.addEventListener("click", () => {
        if (this.swiper.params.loop) {
          this.swiper.slideToLoop(i);
        } else {
          this.swiper.slideTo(i);
        }
      });
      paginationEl.appendChild(dot);
    }

    const updateActiveDot = () => {
      const activeIndex =
        typeof this.swiper.realIndex === "number"
          ? this.swiper.realIndex
          : this.swiper.activeIndex;
      paginationEl
        .querySelectorAll(".slideshow-pagination__dot")
        .forEach((dot, index) => {
          dot.classList.toggle("is-active", index === activeIndex);
          dot.setAttribute("aria-current", index === activeIndex ? "true" : "false");
        });
    };

    this.swiper.on("slideChange", updateActiveDot);
    updateActiveDot();
  }
}

if (!window.customElements.get("slideshow-swiper")) {
  window.customElements.define("slideshow-swiper", SlideshowSwiper);
}

class ComingSoonSlider extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.initSwiper();
  }

  initSwiper() {
    this.swiper = new Swiper(this, {
      slidesPerView: 1.1,
      spaceBetween: 10,
      slidesOffsetBefore: 14,
      slidesOffsetAfter: 14,
      freeMode: true,
      breakpoints: {
        767: {
          slidesPerView: 3.1,
          slidesOffsetBefore: 20,
          slidesOffsetAfter: 20,
        },
      },
    });
  }
}

if (!window.customElements.get("coming-soon-slider")) {
  window.customElements.define("coming-soon-slider", ComingSoonSlider);
}

class WaitlistCountdownTimer extends HTMLElement {
  constructor() {
    super();
    this.intervalId = null;
  }

  connectedCallback() {
    this.expirationDate = this.dataset.expirationDate;
    this.init();
  }

  init() {
    this.initCountdown();
  }

  initCountdown() {
    if (!this.expirationDate) {
      console.warn("No expiration date provided for countdown timer");
      return;
    }

    // Parse the expiration date
    // Expected format: "YYYY-MM-DD HH:MM" (e.g., "2025-12-25 12:00")
    const targetDate = new Date(this.expirationDate);

    if (isNaN(targetDate.getTime())) {
      console.error(
        'Invalid expiration date format. Expected: "YYYY-MM-DD HH:MM" (e.g., "2025-12-25 12:00")'
      );
      return;
    }

    // Start the countdown immediately
    this.updateCountdown(targetDate);

    // Update every second
    this.intervalId = setInterval(() => {
      this.updateCountdown(targetDate);
    }, 1000);
  }

  updateCountdown(targetDate) {
    const now = new Date();
    const timeLeft = targetDate.getTime() - now.getTime();

    if (timeLeft <= 0) {
      // Countdown has ended
      this.displayTime(0, 0, 0, 0);
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
      return;
    }

    // Calculate time components
    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

    this.displayTime(days, hours, minutes, seconds);
  }

  displayTime(days, hours, minutes, seconds) {
    const daysElement = this.querySelector("[data-days]");
    const hoursElement = this.querySelector("[data-hours]");
    const minutesElement = this.querySelector("[data-minutes]");
    const secondsElement = this.querySelector("[data-seconds]");

    if (daysElement) daysElement.textContent = days.toString().padStart(2, "0");
    if (hoursElement)
      hoursElement.textContent = hours.toString().padStart(2, "0");
    if (minutesElement)
      minutesElement.textContent = minutes.toString().padStart(2, "0");
    if (secondsElement)
      secondsElement.textContent = seconds.toString().padStart(2, "0");
  }

  disconnectedCallback() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}

if (!window.customElements.get("waitlist-countdown-timer")) {
  window.customElements.define(
    "waitlist-countdown-timer",
    WaitlistCountdownTimer
  );
}

class WaitlistCollectionCard extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.handleTriggerMouseOver = this.handleTriggerMouseOver.bind(this);
    this.init();
  }

  init() {
    this.triggerElements = this.querySelectorAll("[data-collection-trigger]");
    this.targetElements = this.querySelectorAll("[data-collection-target]");
    this.triggerElements.forEach((el) => {
      el.addEventListener("mouseenter", this.handleTriggerMouseOver);
    });
  }

  handleTriggerMouseOver(e) {
    const trigger = e.currentTarget;
    const targetId = trigger.getAttribute("data-collection-trigger");
    if (!targetId) return;
    this.targetElements.forEach((target) => target.classList.add("hidden"));
    const target = this.querySelector(`[data-collection-target="${targetId}"]`);
    target.classList.remove("hidden");
  }
}

if (!window.customElements.get("waitlist-collection-card")) {
  window.customElements.define(
    "waitlist-collection-card",
    WaitlistCollectionCard
  );
}

class WaysToWear extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.triggerElements = Array.from(this.querySelectorAll("[data-index]"));
    this.nameElements = Array.from(this.querySelectorAll("[data-name-index]"));
    this.initSwiper();
    this.addTriggerListeners();
  }

  initSwiper() {
    this.swiper = new Swiper(this.querySelector("[data-ways-to-wear-slider]"), {
      slidesPerView: 1.1,
      spaceBetween: 10,
      breakpoints: {
        767: {
          slidesPerView: 1,
          spaceBetween: 0,
        },
      },
    });

    const triggerElements = this.triggerElements;
    this.swiper.on("slideChange", (e) => {
      const index = e.activeIndex;
      triggerElements.forEach((el) => {
        el.classList.add("opacity-50");
      });
      if (triggerElements[index]) {
        triggerElements[index].classList.remove("opacity-50");
      }

      this.updateNameElements(index);
    });
  }

  updateNameElements(index) {
    this.nameElements.forEach((el) => {
      el.classList.add("hidden");
    });
    if (this.nameElements[index]) {
      this.nameElements[index].classList.remove("hidden");
    }
  }

  addTriggerListeners() {
    if (!this.triggerElements || !this.swiper) return;
    this.triggerElements.forEach((el, idx) => {
      el.addEventListener("click", (e) => {
        e.preventDefault();
        this.swiper.slideTo(idx);
      });
    });
  }
}

if (!window.customElements.get("ways-to-wear")) {
  window.customElements.define("ways-to-wear", WaysToWear);
}

class KlaviyoForm extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.form = this.querySelector("form");
    this.apiKey = this.dataset.apiKey;
    this.listId = this.dataset.listId;
    this.form.addEventListener("submit", this.handleSubmit.bind(this));
  }

  handleSubmit(event) {
    event.preventDefault();
    this.submit();
  }

  submit() {
    const email = this.form.querySelector('input[name="email"]').value;

    if (!email) {
      this.showErrorMessage("Please enter a valid email address.");
      return;
    }

    if (!this.apiKey) {
      this.showErrorMessage("Klaviyo API key is not configured.");
      return;
    }

    const payload = {
      data: {
        type: "subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: {
                email,
              },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: "list",
              id: this.listId,
            },
          },
        },
      },
    };

    fetch(
      "https://a.klaviyo.com/client/subscriptions?company_id=" + this.apiKey,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          revision: "2024-10-15",
        },
        body: JSON.stringify(payload),
      }
    )
      .then((response) => {
        if (response.ok) {
          this.showSuccessMessage();
        } else {
          return response.json().then((data) => {
            console.error("Klaviyo API error:", data);
            this.showErrorMessage(
              "Sorry, there was an error. Please try again."
            );
          });
        }
      })
      .catch((error) => {
        console.error("Klaviyo subscription error:", error);
        this.showErrorMessage("Sorry, there was an error. Please try again.");
      });
  }

  showSuccessMessage() {
    // Remove any existing messages
    this.clearMessages();

    const successDiv = document.createElement("div");
    successDiv.className = "text-[13px] text-green-600";
    successDiv.textContent = "Thank you for subscribing!";
    this.form.parentNode.insertBefore(successDiv, this.form.nextSibling);
    this.form.reset();
  }

  showErrorMessage(message = "Sorry, there was an error. Please try again.") {
    // Remove any existing messages
    this.clearMessages();

    const errorDiv = document.createElement("div");
    errorDiv.className = "text-[13px] text-red-500";
    errorDiv.textContent = message;
    this.form.parentNode.insertBefore(errorDiv, this.form.nextSibling);
  }

  clearMessages() {
    const existingMessages = this.form.parentNode.querySelectorAll(
      ".text-green-600, .text-red-500"
    );
    existingMessages.forEach((msg) => msg.remove());
  }
}

if (!window.customElements.get("klaviyo-form")) {
  window.customElements.define("klaviyo-form", KlaviyoForm);
}


// Bold Pricing App - Move compare-at price into price-list element
function fixBoldPricingElements() {
  // Find all v-stacks that might contain Bold pricing elements
  document.querySelectorAll('.v-stack, .product-card__info').forEach(container => {
    const moneyDetails = container.querySelector('.money-details:not(.bold-fixed)');
    const priceList = container.querySelector('price-list.money:not(.bold-fixed)');
    
    // Only process if both elements exist and haven't been processed
    if (moneyDetails && priceList) {
      // Get the compare-at price content
      const compareAtContent = moneyDetails.innerHTML;
      
      if (compareAtContent && compareAtContent.trim() !== '') {
        // Create a compare-at-price element
        const comparePriceElement = document.createElement('compare-at-price');
        comparePriceElement.className = 'h6 text-subdued line-through';
        comparePriceElement.style.textDecoration = 'line-through';
        comparePriceElement.style.color = 'rgba(0,0,0,0.65)';
        comparePriceElement.innerHTML = compareAtContent;
        
        // Insert at the beginning of price-list
        priceList.insertBefore(comparePriceElement, priceList.firstChild);
        
        // Remove the money-details div
        moneyDetails.remove();
        
        // Mark as processed
        priceList.classList.add('bold-fixed');
        moneyDetails.classList.add('bold-fixed');
      }
    }
  });
}

// Run on page load
fixBoldPricingElements();

// Use MutationObserver to watch for when Bold injects elements. This sees every DOM change on the page (carousels,
// the cart drawer, chat widgets), so it only rescans when a change touches Bold's markup, at most once a frame.
let boldFixScheduled = false;

const touchesBoldMarkup = (node) => {
  const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
  return Boolean(
    element &&
      (element.closest(".money-details") || element.querySelector?.(".money-details"))
  );
};

const observer = new MutationObserver((mutations) => {
  if (boldFixScheduled) return;
  const relevant = mutations.some((mutation) =>
    Array.prototype.some.call(mutation.addedNodes, touchesBoldMarkup)
  );
  if (!relevant) return;

  boldFixScheduled = true;
  requestAnimationFrame(() => {
    boldFixScheduled = false;
    fixBoldPricingElements();
  });
});

// Start observing
document.addEventListener('DOMContentLoaded', () => {
  fixBoldPricingElements();
  observer.observe(document.body, { childList: true, subtree: true });
});

// ── Upsell custom elements ──────────────────────────────────────────

class UpsellProduct extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('[data-size-select]');
    this.hiddenInput = this.querySelector('input[name*="[id]"]');
    this.priceDivs = this.querySelectorAll('[data-variant-price-display]');

    this.select?.addEventListener('change', () => this.handleChange());
  }

  handleChange() {
    const variantId = this.select.value;
    this.hiddenInput.value = variantId;

    this.priceDivs.forEach((div) => {
      div.classList.toggle('hidden', div.dataset.variantId !== variantId);
    });

    this.dispatchEvent(
      new CustomEvent('upsell:variant-change', { bubbles: true })
    );
  }

  get selectedVariantId() {
    return this.hiddenInput?.value || '';
  }

  get selectedPrice() {
    const visible = [...this.priceDivs].find((d) => !d.classList.contains('hidden'));
    return visible ? parseInt(visible.dataset.variantPrice, 10) || 0 : 0;
  }

  get selectedOriginalPrice() {
    const visible = [...this.priceDivs].find((d) => !d.classList.contains('hidden'));
    return visible ? parseInt(visible.dataset.variantOriginalPrice, 10) || 0 : 0;
  }
}

if (!window.customElements.get('upsell-product')) {
  window.customElements.define('upsell-product', UpsellProduct);
}

class UpsellForm extends HTMLElement {
  connectedCallback() {
    this.atcButton = this.querySelector('[data-atc-button]');
    this.totalEl = this.querySelector('[data-total-price]');

    this.atcButton?.addEventListener('click', () => this.handleSubmit());
    this.addEventListener('upsell:variant-change', () => this.updateTotal());

    this.updateTotal();
  }

  get products() {
    return [...this.querySelectorAll('upsell-product')];
  }

  updateTotal() {
    const total = this.products.reduce((sum, p) => sum + p.selectedPrice, 0);
    const originalTotal = this.products.reduce((sum, p) => sum + p.selectedOriginalPrice, 0);
    const fmt = (v) =>
      window.Shopify?.formatMoney
        ? Shopify.formatMoney(v, window.money_format || '${{amount}}')
        : '$' + (v / 100).toFixed(2);
    if (this.totalEl) {
      if (originalTotal > total) {
        this.totalEl.innerHTML =
          `<span class="line-through">${fmt(originalTotal)}</span> <span class="text-red-500">${fmt(total)}</span>`;
      } else {
        this.totalEl.textContent = fmt(total);
      }
    }
  }

  handleSubmit() {
    const groupId = Date.now().toString();
    const mainProductId = this.dataset.mainProductId;    
    const discountPercentage = this.dataset.discountPercentage || '';
    const productCount = this.dataset.productCount || '';
    const items = this.products
      .filter((p) => p.selectedVariantId)
      .map((p) => {
        const props = { _complete_the_look: groupId,          
          _complete_the_look_number: productCount,
          _complete_the_look_discount: discountPercentage, };
        if (p.dataset.productId === mainProductId) {
          props._complete_the_look_main = 'true';
        }
        return {
          id: parseInt(p.selectedVariantId, 10),
          quantity: 1,
          properties: props,
        };
      });

    if (items.length === 0) {
      alert('Please select a size for at least one product.');
      return;
    }

    document.documentElement.dispatchEvent(
      new CustomEvent('theme:loading:start', { bubbles: true })
    );

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Add to cart failed');
        return res.json();
      })
      .then(() => {
        document.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { baseEvent: 'variant:add' },
          })
        );
        document.querySelector('cart-drawer')?.show();
      })
      .catch((err) => {
        console.error(err);
        alert('Something went wrong adding to cart. Please try again.');
      })
      .finally(() => {
        document.documentElement.dispatchEvent(
          new CustomEvent('theme:loading:end', { bubbles: true })
        );
      });
  }
}

if (!window.customElements.get('upsell-form')) {
  window.customElements.define('upsell-form', UpsellForm);
}

class StickyAtcBar extends HTMLElement {
  constructor() {
    super();
    this._onScroll = this._onScroll.bind(this);
    this._onResize = this._onScroll.bind(this);
    this._onPurchaseClick = this._onPurchaseClick.bind(this);
    this._rafId = null;
  }

  connectedCallback() {
    // Start hidden; JS will show it only after the user scrolls past `buy-buttons`.
    this.classList.add('translate-y-full');
    this._onScroll();
    this.addEventListener('click', this._onPurchaseClick);
    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize, { passive: true });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onPurchaseClick);
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onResize);
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  _getScope() {
    return this.closest('product-rerender') || document;
  }

  _getPurchaseTarget() {
    const scope = this._getScope();
    return (
      scope.querySelector('#pdp-purchase') ||
      scope.querySelector('variant-picker') ||
      scope.querySelector('buy-buttons.buy-buttons')
    );
  }

  _onPurchaseClick(event) {
    const trigger = event.target.closest('[data-scroll-to-purchase]');
    if (!trigger) return;

    event.preventDefault();
    this._scrollToPurchase();
  }

  _scrollToPurchase() {
    const target = this._getPurchaseTarget();
    if (!target) return;

    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth';

    target.scrollIntoView({ block: 'start', behavior });

    window.setTimeout(() => {
      const scope = this._getScope();
      const variantPicker = scope.querySelector('variant-picker');
      if (!variantPicker) return;

      const uncheckedSize = variantPicker.querySelector('input[type="radio"]:not(:checked)');
      if (uncheckedSize && !variantPicker.querySelector('input[type="radio"]:checked')) {
        uncheckedSize.focus({ preventScroll: true });
      }
    }, behavior === 'smooth' ? 400 : 0);
  }

  _getBuyButtons() {
    return this._getScope().querySelector('buy-buttons.buy-buttons');
  }

  _onScroll() {
    if (!this.isConnected) return;
    if (this._rafId) return;

    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      const buyButtons = this._getBuyButtons();
      if (!buyButtons) return;

      const rect = buyButtons.getBoundingClientRect();

      // "Scrolled past" means the bottom edge of the buy buttons is above the viewport.
      // When this becomes true, we hide the sticky offset transition by removing `translate-y-full`.
      const hasScrolledPast = rect.bottom <= 0;
      this.classList.toggle('translate-y-full', !hasScrolledPast);
    });
  }
}

if (!window.customElements.get('sticky-atc-bar')) {
  window.customElements.define('sticky-atc-bar', StickyAtcBar);
}

/* ROYAL: open the inline quick-add on ANY card. The standard <product-card> has no <new-product-card>
   delegated trigger, and quick-add's own direct listener can miss when children parse after connectedCallback.
   This delegated handler opens it everywhere; QuickAdd handles close (mouseleave/scroll), QuickAddForm the add + redirect to cart. */
(function () {
  if (window.__qaTriggerGlobal) return;
  window.__qaTriggerGlobal = true;
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-quick-add-trigger]');
    if (!trigger) return;
    e.preventDefault();
    var qa = trigger.closest('quick-add');
    if (!qa) return;
    document.querySelectorAll('quick-add.active').forEach(function (el) { if (el !== qa) el.classList.remove('active'); });
    if (typeof qa.show === 'function') qa.show(); else qa.classList.add('active');
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var qa = document.querySelector('quick-add.active');
    if (qa && typeof qa.hide === 'function') qa.hide();
  });
})();

/* Keep empty-cart recommendations from being vertically centered into the header. */
(function () {
  function syncEmptyCartDrawer(drawer) {
    if (!drawer || !drawer.querySelector('.cart-drawer__empty-recs')) return;
    drawer.classList.remove('drawer--center-body');
  }
  document.addEventListener('cart-drawer:refreshed', function (e) {
    syncEmptyCartDrawer(e.target);
  });
  document.addEventListener('DOMContentLoaded', function () {
    syncEmptyCartDrawer(document.querySelector('cart-drawer'));
  });
})();

/* PDP accordion engagement. Only drawers with data-accordion-id are tracked.
   accordion_name is the visible title, so a rename updates reporting without a code change. */
document.addEventListener('toggle', (event) => {
  const details = event.target;
  if (!(details instanceof HTMLDetailsElement)) return;
  const accordionId = details.dataset.accordionId;
  if (!accordionId) return;

  const title = details.querySelector('[data-accordion-title]');
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'accordion_toggle',
    accordion_id: accordionId,
    accordion_name: (title?.textContent || '').replace(/\s+/g, ' ').trim(),
    accordion_action: details.open ? 'expand' : 'collapse',
    item_id: details.dataset.itemId || '',
    item_name: details.dataset.itemName || '',
  });
}, true);

/* PDP rating summary → scroll to Okendo reviews section.
   Prefer #product-reviews; never fall back to star-rating chips (.oke-sr),
   which sit near the top of the PDP and make the click look like a no-op. */
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-scroll-to-reviews], a[href="#product-reviews"]');
  if (!trigger) return;

  event.preventDefault();

  const target =
    document.getElementById('product-reviews') ||
    document.querySelector('[data-product-reviews-anchor]') ||
    document.querySelector('[data-oke-reviews-widget]') ||
    document.querySelector('[id*="okendo_reviews_widget"]') ||
    document.querySelector('.okeReviews.oke-w') ||
    document.querySelector('.shopify-section--apps .okeReviews:not(.oke-sr)');

  if (!target) return;

  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  if (history.replaceState) {
    history.replaceState(null, '', '#product-reviews');
  }
});

/* ---------------------------------------------------------------------------
 * Bundle builder (sections/bundle.liquid)
 * ------------------------------------------------------------------------ */

/* Every line of one bundle carries the same _bundleGroupId. Returns a Map of group id to that
   bundle's line items, skipping ordinary cart items. */
function groupBundleLines(cart) {
  const groups = new Map();

  cart.items.forEach((item) => {
    const groupId = item.properties?._bundleGroupId;
    if (!groupId) return;
    if (!groups.has(groupId)) groups.set(groupId, []);
    groups.get(groupId).push(item);
  });

  return groups;
}

class BundleBuilder extends HTMLElement {
  connectedCallback() {
    this.numberOfRows = parseInt(this.dataset.numberOfRows);
    this.bundleVariantId = this.dataset.bundleVariantId;
    this.freeGiftVariantId = this.dataset.freeGiftVariantId;
    this.freeGiftPrice = parseInt(this.dataset.freeGiftPrice) || 0;
    this.bundleDiscounts = this.dataset.bundleDiscounts.split(",").map(Number);
    this.bundlesInCart = parseInt(this.dataset.bundlesInCart);
    this.atcButtons = this.querySelectorAll("[data-bundle-add-product]");
    this.resetButtons = this.querySelectorAll("[data-reset-bundle]");
    this.chosenProductRows = this.querySelectorAll("[data-row-chosen-product]");
    this.colour = null;

    this.clearChosenVariants();
    this.render();
    this.bindEvents();
  }

  bindEvents() {
    this.addEventListener("addToBundle", (e) => this.addToBundle(e));

    // A filtered row comes back as fresh cards, which have lost the added state.
    this.addEventListener("bundle-row:rendered", () => this.renderCardButtons());

    /* Which tier this bundle lands on depends on how many are already in the cart, so the overview
       reprices whenever the cart moves — including after its own add, which refreshes the cart. */
    ["cart:change", "cart:refresh"].forEach((event) =>
      document.addEventListener(event, () => this.refreshBundlesInCart())
    );

    this.querySelectorAll("[data-bundle-remove-product]").forEach((button) => {
      button.addEventListener("click", (e) => {
        e.stopPropagation();
        this.removeFromBundle(
          e.target.closest("[data-row-chosen-product]").dataset.rowChosenProduct
        );
      });
    });

    this.resetButtons.forEach((el) =>
      el.addEventListener("click", () => this.resetBundle())
    );
    this.atcButtons.forEach((el) =>
      el.addEventListener("click", () => this.addBundleToCart())
    );
    this.chosenProductRows.forEach((el) =>
      el.addEventListener("click", () => this.focusRow(el.dataset.rowChosenProduct))
    );
  }

  clearChosenVariants() {
    this.chosenVariants = Array.from({ length: this.numberOfRows }).reduce(
      (acc, _, i) => {
        acc[i] = null;
        return acc;
      },
      {}
    );
  }

  resetBundle() {
    this.clearChosenVariants();
    this.querySelectorAll("[data-bundle-row-index]").forEach((row) =>
      this.clearRowSelection(row.dataset.bundleRowIndex)
    );
    this.render();
  }

  /* Emptying a slot has to clear that row's option inputs too. A checked radio
     fires no change event when clicked again, so without this the customer
     cannot re-pick the size they just removed. */
  clearRowSelection(rowIndex) {
    this.querySelectorAll(
      `[data-bundle-row-index="${rowIndex}"] input[type="radio"]:checked`
    ).forEach((input) => {
      input.checked = false;
      input.removeAttribute("checked");
    });
  }

  addToBundle(e) {
    const { rowIndex, product, variant } = e.detail;
    this.chosenVariants[rowIndex] = { product, variant };
    this.render();
    this.focusRow(Number(rowIndex) + 1);

    /* The pick that fills the last slot has no next row to move on to, so the overview opens
       instead — the finished bundle and its Add to Cart button in front of the customer rather
       than folded away behind the summary bar. */
    if (this.complete) this.openMobileOverview();
  }

  removeFromBundle(rowIndex) {
    this.chosenVariants[rowIndex] = null;
    this.clearRowSelection(rowIndex);
    this.render();
  }

  focusRow(rowIndex) {
    this.querySelectorAll("[data-bundle-row-index]").forEach((r) => {
      const details = r.querySelector("details");
      if (details) {
        details.open = r.dataset.bundleRowIndex == rowIndex;
      }
    });

    this.closeMobileOverview();

    const row = this.querySelector(`[data-bundle-row-index="${rowIndex}"]`);
    if (!row) return;

    const offset = window.matchMedia("(min-width: 48em)").matches ? 168 : 68;
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    window.scrollTo({
      top: scrollTop + row.getBoundingClientRect().top - offset,
      behavior: "smooth",
    });
  }

  openMobileOverview() {
    this.querySelector("bundle-overview-mobile")?.open();
  }

  closeMobileOverview() {
    this.querySelector("bundle-overview-mobile")?.close();
  }

  get complete() {
    return Object.values(this.chosenVariants).every(Boolean);
  }

  /* Adding this bundle puts the whole cart on the rate for one more than it already holds — so the
     entry after the ones already earned, or the last entry once past the end of the list. */
  get discountPercentage() {
    return this.bundleDiscounts[Math.min(this.bundlesInCart, this.bundleDiscounts.length - 1)];
  }

  refreshBundlesInCart() {
    fetch("/cart.js")
      .then((res) => res.json())
      .then((cart) => {
        this.bundlesInCart = groupBundleLines(cart).size;
        this.renderTotal();
        this.renderDiscountPercentage();
      })
      .catch((err) => console.error(err));
  }

  /* One colour across the whole builder: picking one in a row's swatches picks it in every other
     row whose collection stocks it, and puts a row that doesn't stock it back to showing
     everything. Passing null clears the colour everywhere. */
  shareColour(value) {
    this.colour = value;
    this.querySelectorAll("bundle-filters").forEach((filters) => filters.apply(value));
  }

  addBundleToCart() {
    if (!this.complete) {
      this.closeMobileOverview();
      return;
    }

    const chosen = Object.values(this.chosenVariants);

    const bundleGroupId =
      Date.now() + "-" + Math.random().toString(36).substring(2, 11);

    /* _bundleVariantId is the variant the cart transform merges these lines into. _bundleSize lets
       it tell a whole bundle from part of one. _bundleRow only has to be unique per line: without it
       two rows landing on the same variant would merge into a single line and the bundle would look
       short. */
    const items = chosen.map(({ variant }, rowIndex) => ({
      id: variant.id,
      quantity: 1,
      properties: {
        _bundleGroupId: bundleGroupId,
        _bundleVariantId: Number(this.bundleVariantId),
        _bundleSize: chosen.length,
        _bundleRow: rowIndex,
      },
    }));

    /* The gift rides alongside as its own line rather than inside the merge, so the bundle-free-gift
       discount function can take it to zero. It carries the bundle's group id under its own key — under
       _bundleGroupId the cart transform would count it as one of the bundle's lines and never merge. */
    if (this.freeGiftVariantId) {
      items.push({
        id: Number(this.freeGiftVariantId),
        quantity: 1,
        properties: { _bundleFreeGift: bundleGroupId },
      });
    }

    document.documentElement.dispatchEvent(
      new CustomEvent("theme:loading:start", { bubbles: true })
    );

    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items }),
    })
      .then((res) => res.json())
      .then(() => {
        document.dispatchEvent(
          new CustomEvent("cart:refresh", {
            bubbles: true,
            detail: { baseEvent: "variant:add" },
          })
        );
        this.resetBundle();
        this.closeMobileOverview();
        document.querySelector("cart-drawer")?.show();
      })
      .catch((err) => console.error(err))
      .finally(() => {
        document.documentElement.dispatchEvent(
          new CustomEvent("theme:loading:end", { bubbles: true })
        );
      });
  }

  render() {
    this.renderCardButtons();
    this.renderChosenProducts();
    this.renderTotal();
    this.renderButton();
    this.renderDiscountPercentage();
  }

  // Fills every [bundle_discount] the merchant wrote into a banner or text block.
  renderDiscountPercentage() {
    this.updateAllEls("[data-bundle-discount-percentage]", this.discountPercentage);
  }

  renderCardButtons() {
    const chosenProductIds = Object.values(this.chosenVariants)
      .filter(Boolean)
      .map((item) => item.product.id.toString());

    this.querySelectorAll("[data-product-id]").forEach((card) => {
      card
        .querySelector("[data-atc-button]")
        ?.classList.toggle("added", chosenProductIds.includes(card.dataset.productId));
    });
  }

  renderChosenProducts() {
    this.querySelectorAll("[data-row-chosen-product]").forEach((el) => {
      const chosen = this.chosenVariants[el.dataset.rowChosenProduct];
      const innerContent = el.querySelector("[data-inner-content]");
      const titleAndOptions = el.querySelector("[data-title-and-options]");
      const removeButton = el.querySelector("[data-bundle-remove-product]");

      if (!chosen) {
        innerContent.innerHTML = innerContent.dataset.title;
        titleAndOptions.innerHTML = "";
        removeButton.classList.add("hidden");
        return;
      }

      const { product, variant } = chosen;
      innerContent.innerHTML = `<img src="${variant.imageUrl}" alt="${product.title}" />`;
      removeButton.classList.remove("hidden");
      titleAndOptions.innerHTML = `
        <div class="bundle-chosen-product-title">${product.title}</div>
        ${product.options
          .map(
            (option, index) =>
              `<p class="bundle-chosen-product-option">${option}: ${variant.options[index]}</p>`
          )
          .join("")}`;
    });
  }

  renderTotal() {
    const total = Object.values(this.chosenVariants)
      .filter(Boolean)
      .reduce((acc, { variant }) => acc + variant.price, 0);

    const percentage = this.discountPercentage;

    // Taken off the rounded price the customer is charged, so the two lines add back up to the total.
    const final = Math.round((total * (100 - percentage)) / 100);
    const discount = total - final;
    // The free gift is part of what the bundle saves, though it isn't part of the total.
    const savings = discount + this.freeGiftPrice;

    this.updateAllEls("[data-bundle-final-price]", this.formatMoney(final));
    this.updateAllEls(
      "[data-bundle-original-price]",
      discount > 0 ? this.formatMoney(total) : ""
    );
    // Named only once the bundle is whole, since a part-built one hasn't earned the saving yet.
    this.updateAllEls("[data-bundle-savings-amount]", this.formatMoney(savings));
    this.querySelectorAll("[data-bundle-savings]").forEach(
      (el) => (el.hidden = !this.complete || savings === 0)
    );
  }

  renderButton() {
    this.atcButtons.forEach((el) => el.classList.toggle("disabled", !this.complete));
  }

  formatMoney(cents) {
    const format = window.themeVariables.settings.moneyFormat;

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, placeholder) => {
      const decimals = placeholder.includes("no_decimals") ? 0 : 2;
      const thousands = placeholder.includes("comma_separator")
        ? "."
        : placeholder.includes("apostrophe_separator")
        ? "'"
        : ",";
      const decimal = thousands === "." ? "," : ".";

      const [whole, fraction] = (cents / 100).toFixed(decimals).split(".");
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

      return fraction ? grouped + decimal + fraction : grouped;
    });
  }

  updateAllEls(selector, value) {
    this.querySelectorAll(selector).forEach((el) => (el.innerHTML = value));
  }
}

if (!window.customElements.get("bundle-builder")) {
  window.customElements.define("bundle-builder", BundleBuilder);
}

/* A free gift is only free while the bundle it came with is in the cart, so once that bundle is
   removed its gift would sit there at full price. Take it out with it. Resolves to whether it removed
   anything, so the caller can redraw the cart. */
function removeOrphanedBundleGifts(cart) {
  const bundles = groupBundleLines(cart);
  const updates = {};
  cart.items.forEach((item) => {
    const groupId = item.properties?._bundleFreeGift;
    if (groupId && !bundles.has(groupId)) updates[item.key] = 0;
  });
  if (!Object.keys(updates).length) return Promise.resolve(false);

  return fetch("/cart/update.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ updates }),
  }).then(() => true);
}

/* Every cart edit in the drawer fires cart:change with the cart it left behind. The refresh that
   follows a removal finds no orphans, so this settles after one pass. */
document.addEventListener("cart:change", (event) => {
  const cart = event.detail?.cart;
  if (!cart?.items) return;

  removeOrphanedBundleGifts(cart)
    .then((removed) => {
      if (removed) document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    })
    .catch((err) => console.error(err));
});

/* The cart page edits by full page load rather than cart:change, so it checks on arrival instead
   and reloads to drop the gift it removed. */
if (window.themeVariables?.settings?.pageType === "cart") {
  fetch("/cart.js")
    .then((res) => res.json())
    .then(removeOrphanedBundleGifts)
    .then((removed) => {
      if (removed) window.location.reload();
    })
    .catch((err) => console.error(err));
}

/* The colour swatches inside one collection-backed row. Storefront filter values only exist in a
   collection request, so the colours can't come from the product template's Liquid — the row asks
   its own collection for them through the collection.bundle-filters view, which carries no products
   and so lands well ahead of the row itself. A pick is handed to the builder, which shares it with
   every row. */
class BundleFilters extends HTMLElement {
  connectedCallback() {
    this.swatchRow = this.querySelector("[data-filter-swatches]");
    this.label = this.querySelector("[data-filter-label]");
    this.skeleton = this.querySelector("[data-filter-skeleton]");
    this.clearButton = this.querySelector("[data-clear-filters]");
    this.builder = this.closest("bundle-builder");
    this.row = this.closest("[data-bundle-row-index]");
    this.selected = null;

    this.clearButton.addEventListener("click", () => this.pick(null));
    this.collect();
  }

  collect() {
    const target = new URL(this.dataset.collectionUrl, window.location.origin);
    target.searchParams.set("view", "bundle-filters");

    fetch(target)
      .then((res) => res.text())
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, "text/html");
        const swatches = Array.from(parsed.querySelectorAll("[data-filter-value]"));
        this.sort(swatches).forEach((swatch) => {
          swatch.addEventListener("click", () => this.pick(swatch));
          swatch.addEventListener("mouseenter", () => this.nameColour(swatch));
          swatch.addEventListener("mouseleave", () => this.nameColour(null));
          this.swatchRow.appendChild(swatch);
        });
        this.settle();
      });
  }

  /* The bundle product names the colours in the order it wants them, so every row lays the same
     colour out in the same place. A colour it doesn't name keeps the place its collection gave it,
     after the ones it does — the sort is stable, and an unnamed colour ranks past the end of the
     list. Both the order and the filter values are Colour metaobjects, so they are matched on the
     metaobject id carried in the filter value's gid. */
  sort(swatches) {
    const order = this.dataset.colourOrder.split(",").filter(Boolean);

    const rank = (swatch) => {
      const place = order.indexOf(swatch.dataset.filterValue.split("/").pop());
      return place === -1 ? order.length : place;
    };

    return swatches.sort((a, b) => rank(a) - rank(b));
  }

  settle() {
    this.skeleton.remove();
    this.hidden = this.swatchRow.children.length === 0;

    // A colour can be picked in another row before this collection's swatches land.
    this.apply(this.builder.colour);
  }

  /* One colour at a time: a pick replaces whatever was chosen, and picking the chosen one again
     clears it. Passing null clears too — that is the clear button. */
  pick(swatch) {
    const picked = swatch && swatch !== this.selected ? swatch : null;
    this.builder.shareColour(picked && picked.dataset.filterValue);
  }

  /* Shows the shared colour as picked if this collection stocks it, and reloads the row when that
     changes what it is showing. Collections name the colour filter differently, so a colour is
     matched across rows by its value and each row filters with its own parameter. */
  apply(value) {
    const picked = (value && this.swatchFor(value)) || null;
    if (picked === this.selected) return;
    this.selected = picked;

    this.querySelectorAll("[data-filter-value]").forEach((el) =>
      el.classList.toggle("active", el === picked)
    );
    this.nameColour(null);
    this.clearButton.classList.toggle("hidden", !picked);

    this.row.querySelector("bundle-collection-row").load();
  }

  /* Names the colour under the cursor, the way the colour swatches on a product page name theirs,
     so a colour can be read without picking it. Passing null names the picked colour again — that
     is the cursor leaving a swatch, and it leaves the label blank while nothing is picked. */
  nameColour(swatch) {
    const named = swatch || this.selected;
    this.label.textContent = named ? named.dataset.filterLabel : "";
  }

  swatchFor(value) {
    return Array.from(this.swatchRow.querySelectorAll("[data-filter-value]")).find(
      (el) => el.dataset.filterValue.toLowerCase() === value.toLowerCase()
    );
  }

  get params() {
    if (!this.selected) return [];
    const { filterParam, filterValue } = this.selected.dataset;
    return [[filterParam, filterValue]];
  }
}

if (!window.customElements.get("bundle-filters")) {
  window.customElements.define("bundle-filters", BundleFilters);
}

/* A collection-backed bundle row. The collection renders through its own
   collection.bundle-row template, so Shopify's storefront filters narrow it down
   server side — a big collection is never shipped to the browser to be filtered.
   The colours it filters by are the swatches sitting above it in the same row.
   The products come back best-selling first, so every row leads with its most popular items
   whatever order the collection itself is set to. */
class BundleCollectionRow extends HTMLElement {
  connectedCallback() {
    this.filters = this.closest("[data-bundle-row-index]").querySelector("bundle-filters");
    this.skeleton = this.innerHTML;
    this.load();
  }

  load() {
    const target = new URL(this.dataset.collectionUrl, window.location.origin);
    target.searchParams.set("view", "bundle-row");
    target.searchParams.set("sort_by", "best-selling");
    this.filters.params.forEach(([param, value]) =>
      target.searchParams.append(param, value)
    );

    this.innerHTML = this.skeleton;

    fetch(target)
      .then((res) => res.text())
      .then((html) => {
        this.innerHTML = html;
        this.dispatchEvent(
          new CustomEvent("bundle-row:rendered", { bubbles: true })
        );
      });
  }
}

if (!window.customElements.get("bundle-collection-row")) {
  window.customElements.define("bundle-collection-row", BundleCollectionRow);
}

class BundleOverviewMobile extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll("[data-summary-toggle]").forEach((toggle) =>
      toggle.addEventListener("click", () => this.classList.toggle("active"))
    );
  }

  open() {
    this.classList.add("active");
  }

  close() {
    this.classList.remove("active");
  }
}

if (!window.customElements.get("bundle-overview-mobile")) {
  window.customElements.define("bundle-overview-mobile", BundleOverviewMobile);
}

/* Klaviyo Back in Stock subscribes to the variant chosen in its popup. The popup
   does not follow this theme's size picker, so pin it to the variant on the button. */
document.addEventListener("click", function (event) {
  const trigger = event.target.closest(".klaviyo-bis-trigger, .klaviyo-rc-bis-anchor");
  if (!trigger) return;

  const variantId = trigger.getAttribute("data-variant-id");
  if (!variantId) return;

  const form = trigger.closest("form") || document.querySelector('form[action*="/cart/add"]');
  const variantInput = form && form.querySelector('[name="id"]');
  if (variantInput) variantInput.value = variantId;

  const url = new URL(window.location.href);
  url.searchParams.set("variant", variantId);
  history.replaceState({}, "", url);

  let attempts = 0;
  const timer = window.setInterval(function () {
    attempts += 1;
    let applied = false;
    try {
      const frame = window.frames["klaviyo-bis-iframe"];
      const select = frame && frame.document && frame.document.querySelector("#variants");
      const option = select && select.querySelector('option[value="' + variantId + '"]');
      if (option) {
        select.value = variantId;
        option.selected = true;
        select.dispatchEvent(new Event("change", { bubbles: true }));
        applied = true;
      }
    } catch (error) {
      applied = false;
    }
    if (applied || attempts > 20) window.clearInterval(timer);
  }, 100);
}, true);

